import React, { useEffect, useState } from "react";
import { usePost } from "redux/fetch-hooks";
import { Reaction } from "workstep/pages/Feedback/Comments/state/types";
import { useErrorToast, useDefaultToast } from "lib/hooks/toasts";
import ReactionBadge from "common/DS/ReactionBadge";

type Answer = {
    question_text: string;
    response: Maybe<string>;
    comment: string;
};
type Reply = {
    replyId: number;
    answers: Answer[];
    reaction: Maybe<Reaction>;
    content: string;
};
type BatchReplyProps = {
    replies: Reply[];
    companyName: string;
    showToast: boolean;
    enableReactions: boolean;
};

const BatchReply = ({ replies, companyName, showToast, enableReactions }: BatchReplyProps) => {
    const errorToast = useErrorToast();
    const defaultToast = useDefaultToast();
    const [repliesState, setRepliesState] = useState(replies);
    const [setReaction, { isLoading }] = usePost<{ reaction: Maybe<Reaction>; replyId: number }>(
        window.location.toString(),
        {
            onError: () => {
                errorToast("There was an error saving your reaction");
            },
        },
    );
    useEffect(() => {
        if (showToast && repliesState.some((r) => r.reaction)) {
            defaultToast(`Your reaction has been saved`, {
                anchorOrigin: {
                    vertical: "top",
                    horizontal: "right",
                },
            });
        }
    }, []);

    const onReact = (replyId: number, val: Maybe<Reaction>) => {
        setReaction({
            data: { reaction_type: val?.type || null, reply_id: replyId, add_reaction: true },
            onSuccess: (data) => {
                setRepliesState((old) => [
                    ...old.map((reply) => {
                        if (reply.replyId === data.replyId) {
                            return { ...reply, reaction: data.reaction };
                        }
                        return reply;
                    }),
                ]);
                if (data.reaction) {
                    defaultToast(`Your ${data.reaction?.emoji} has been saved`, {
                        anchorOrigin: {
                            vertical: "top",
                            horizontal: "right",
                        },
                    });
                }
            },
        });
    };
    return (
        <div className="check-in check-in-reply">
            <div className="check-in__questions">
                <div className="questions__card-group">
                    <div className="check-in-reply__card">
                        <p className="check-in-reply__text">
                            Your recent feedback was shared confidentially with the {companyName}{" "}
                            team. They have provided the following response:
                        </p>

                        {repliesState.map((r) => (
                            <div className="pulse-comment" key={r.replyId}>
                                {r.answers.map((answer) => (
                                    <div key={answer.question_text}>
                                        <div className="pulse-comment__question">
                                            Q: {answer.question_text}
                                        </div>
                                        {answer.response && (
                                            <div className="flex flex-ac flex-jsb pulse-comment__answer">
                                                <span className="pulse-comment__selection-text">
                                                    {answer.response}
                                                </span>
                                            </div>
                                        )}
                                        <blockquote className="pulse-comment__text" data-private>
                                            &quot;{answer.comment}&quot;
                                        </blockquote>
                                    </div>
                                ))}

                                <div className="pulse-comment__reply prel">
                                    <div className="flex flex-ac flex-jsb">
                                        <div className="pulse-comment__reply-company-text">
                                            The {companyName} team replied:
                                        </div>
                                    </div>
                                    <div
                                        className="pulse-comment__reply-text"
                                        // eslint-disable-next-line react/no-danger
                                        dangerouslySetInnerHTML={{
                                            __html: r.content,
                                        }}
                                    />
                                    {enableReactions && (
                                        <ReactionBadge
                                            reaction={r.reaction}
                                            isLoading={isLoading}
                                            onChange={(val) => onReact(r.replyId, val)}
                                            side="left"
                                        />
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
export default BatchReply;
